import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import MonthlyLeadersScreen from './minisite/MonthlyLeadersScreen';
import RemoteNameKeyboard from './minisite/RemoteNameKeyboard';
import { fetchMinisiteLeaderboard, submitMinisiteLeaderboard } from '../api/minisiteLeaderboard';
import { qualifiesForLeaderboard } from '../utils/minisiteLeaderboardQualify';
import './SnakeGame.css';
import './minisite/MinisiteShared.css';

const COLS = 30;
const ROWS = 22;

const DIR = {
  ArrowUp:    { x: 0,  y: -1 },
  ArrowDown:  { x: 0,  y:  1 },
  ArrowLeft:  { x: -1, y:  0 },
  ArrowRight: { x: 1,  y:  0 },
};

const OPPOSITE = {
  ArrowUp: 'ArrowDown', ArrowDown: 'ArrowUp',
  ArrowLeft: 'ArrowRight', ArrowRight: 'ArrowLeft',
};

const COLORS = {
  bg: '#0d1117',
  grid: '#161b22',
  snakeHead: '#4ade80',
  snakeBody: '#22c55e',
  snakeBorder: '#166534',
  food: '#f97316',
  foodGlow: '#ea580c',
};

// Header ~56px + footer ~28px + padding ~16px
const CHROME_H = 118;
const CHROME_W = 16;

function computeCell() {
  const maxW = Math.max(window.innerWidth  - CHROME_W, 100);
  const maxH = Math.max(window.innerHeight - CHROME_H, 100);
  return Math.max(8, Math.min(Math.floor(maxW / COLS), Math.floor(maxH / ROWS)));
}

function randomCell(snake, cols, rows) {
  while (true) {
    const pos = {
      x: Math.floor(Math.random() * cols),
      y: Math.floor(Math.random() * rows),
    };
    if (!snake.some(s => s.x === pos.x && s.y === pos.y)) return pos;
  }
}

function SnakeGame() {
  useMinisiteSessionAnalytics();
  const canvasRef  = useRef(null);
  const cellRef    = useRef(computeCell());
  const [canvasW, setCanvasW] = useState(() => cellRef.current * COLS);
  const [canvasH, setCanvasH] = useState(() => cellRef.current * ROWS);

  const stateRef = useRef({
    snake: [],
    dir: { x: 1, y: 0 },
    nextDir: { x: 1, y: 0 },
    food: null,
    score: 0,
    highScore: 0,
    phase: 'idle',
    frameId: null,
    lastTime: 0,
    interval: 130,
  });

  const [displayScore, setDisplayScore] = useState(0);
  const [displayHigh,  setDisplayHigh]  = useState(0);
  const [phase, setPhase] = useState('idle');

  const [screen, setScreen] = useState('leaders');
  const [deadStep, setDeadStep] = useState('idle');
  const [joinSel, setJoinSel] = useState('no');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const goToLeaders = useCallback(() => {
    const s = stateRef.current;
    cancelAnimationFrame(s.frameId);
    s.phase = 'idle';
    s.frameId = null;
    setPhase('idle');
    setDeadStep('idle');
    setNameInput('');
    setJoinSel('no');
    setScreen('leaders');
  }, []);

  // Resize handler — recalculates cell size and resizes the canvas
  useEffect(() => {
    const onResize = () => {
      const c = computeCell();
      cellRef.current = c;
      setCanvasW(c * COLS);
      setCanvasH(c * ROWS);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx  = canvas.getContext('2d');
    const cell = cellRef.current;
    const w    = cell * COLS;
    const h    = cell * ROWS;
    const { snake, food } = stateRef.current;

    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth   = 0.5;
    for (let x = 0; x <= COLS; x++) {
      ctx.beginPath(); ctx.moveTo(x * cell, 0); ctx.lineTo(x * cell, h); ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      ctx.beginPath(); ctx.moveTo(0, y * cell); ctx.lineTo(w, y * cell); ctx.stroke();
    }

    // Food
    if (food) {
      const fx = food.x * cell + cell / 2;
      const fy = food.y * cell + cell / 2;
      const grad = ctx.createRadialGradient(fx, fy, 1, fx, fy, cell / 2);
      grad.addColorStop(0, '#ffedd5');
      grad.addColorStop(0.5, COLORS.food);
      grad.addColorStop(1, COLORS.foodGlow);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(fx, fy, cell / 2 - 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Snake
    snake.forEach((seg, i) => {
      const isHead = i === 0;
      const r = isHead ? Math.max(2, cell * 0.25) : Math.max(1, cell * 0.15);
      ctx.fillStyle   = isHead ? COLORS.snakeHead : COLORS.snakeBody;
      ctx.strokeStyle = COLORS.snakeBorder;
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.roundRect(seg.x * cell + 1, seg.y * cell + 1, cell - 2, cell - 2, r);
      ctx.fill();
      ctx.stroke();
    });
  }, []);

  const startGame = useCallback(() => {
    const s = stateRef.current;
    cancelAnimationFrame(s.frameId);
    s.frameId = null;
    const initSnake = [
      { x: 5, y: Math.floor(ROWS / 2) },
      { x: 4, y: Math.floor(ROWS / 2) },
      { x: 3, y: Math.floor(ROWS / 2) },
    ];
    s.snake    = initSnake;
    s.dir      = { x: 1, y: 0 };
    s.nextDir  = { x: 1, y: 0 };
    s.food     = randomCell(initSnake, COLS, ROWS);
    s.score    = 0;
    s.phase    = 'running';
    s.lastTime = 0;
    s.interval = 130;
    setDisplayScore(0);
    setPhase('running');
    draw();
  }, [draw]);

  const gameLoop = useCallback((timestamp) => {
    const s = stateRef.current;
    if (s.phase !== 'running') return;

    if (timestamp - s.lastTime >= s.interval) {
      s.lastTime = timestamp;
      s.dir = { ...s.nextDir };

      const head = s.snake[0];
      const next = { x: head.x + s.dir.x, y: head.y + s.dir.y };

      if (next.x < 0 || next.x >= COLS || next.y < 0 || next.y >= ROWS) {
        s.phase = 'dead';
        if (s.score > s.highScore) s.highScore = s.score;
        setDisplayHigh(s.highScore);
        setPhase('dead');
        draw();
        return;
      }

      const bodyWithoutTail = s.snake.slice(0, s.snake.length - 1);
      if (bodyWithoutTail.some(seg => seg.x === next.x && seg.y === next.y)) {
        s.phase = 'dead';
        if (s.score > s.highScore) s.highScore = s.score;
        setDisplayHigh(s.highScore);
        setPhase('dead');
        draw();
        return;
      }

      s.snake.unshift(next);

      if (next.x === s.food.x && next.y === s.food.y) {
        s.score   += 10;
        s.food     = randomCell(s.snake, COLS, ROWS);
        s.interval = Math.max(60, 130 - Math.floor(s.score / 50) * 10);
        setDisplayScore(s.score);
      } else {
        s.snake.pop();
      }

      draw();
    }

    s.frameId = requestAnimationFrame(gameLoop);
  }, [draw]);

  // Redraw whenever canvas dimensions change (resize)
  useEffect(() => { draw(); }, [draw, canvasW, canvasH]);

  useLayoutEffect(() => {
    if (screen !== 'game') return undefined;
    const c = computeCell();
    cellRef.current = c;
    const w = c * COLS;
    const h = c * ROWS;
    setCanvasW(w);
    setCanvasH(h);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = w;
      canvas.height = h;
    }
    startGame();
    const s = stateRef.current;
    cancelAnimationFrame(s.frameId);
    s.frameId = requestAnimationFrame(gameLoop);
    return () => {
      cancelAnimationFrame(s.frameId);
      s.frameId = null;
    };
  }, [screen, startGame, gameLoop]);

  useEffect(() => {
    if (phase !== 'dead') return undefined;
    let cancelled = false;
    setDeadStep('loading');
    (async () => {
      try {
        const d = await fetchMinisiteLeaderboard('snake');
        if (cancelled) return;
        const entries = d.entries || [];
        const sc = stateRef.current.score;
        const show = qualifiesForLeaderboard('snake', entries, sc);
        setDeadStep(show ? 'joinAsk' : 'restartOnly');
      } catch {
        if (!cancelled) setDeadStep('restartOnly');
      }
    })();
    return () => { cancelled = true; };
  }, [phase]);

  useEffect(() => {
    if (phase === 'running') {
      setDeadStep('idle');
    }
  }, [phase]);

  useEffect(() => {
    const onKey = (e) => {
      const s = stateRef.current;

      if (deadStep === 'name') {
        return;
      }

      if (phase === 'dead' && deadStep === 'loading') {
        e.preventDefault();
        return;
      }

      if (phase === 'dead' && deadStep === 'joinAsk') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setJoinSel((v) => (v === 'no' ? 'yes' : 'no'));
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          if (joinSel === 'yes') {
            setNameInput('');
            setDeadStep('name');
            return;
          }
          goToLeaders();
        }
        return;
      }

      if (phase === 'dead' && deadStep === 'thanks') {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (e.key === 'Enter' || e.keyCode === 13) {
        if (phase === 'dead' && deadStep === 'restartOnly') {
          cancelAnimationFrame(s.frameId);
          startGame();
          s.frameId = requestAnimationFrame(gameLoop);
        } else if (s.phase === 'idle') {
          cancelAnimationFrame(s.frameId);
          startGame();
          s.frameId = requestAnimationFrame(gameLoop);
        }
        e.preventDefault();
        return;
      }

      if (DIR[e.key] && s.phase === 'running') {
        const currentDirKey = Object.keys(DIR).find(
          k => DIR[k].x === s.dir.x && DIR[k].y === s.dir.y
        );
        if (OPPOSITE[e.key] !== currentDirKey) {
          s.nextDir = { ...DIR[e.key] };
        }
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [startGame, gameLoop, phase, deadStep, joinSel, goToLeaders]);

  const handleNameDone = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await submitMinisiteLeaderboard('snake', trimmed, stateRef.current.score);
      setDeadStep('thanks');
    } catch {
      setDeadStep('thanks');
    } finally {
      setSubmitting(false);
    }
  }, [nameInput, submitting]);

  const handleNameCancel = useCallback(() => {
    setDeadStep('joinAsk');
    setNameInput('');
  }, []);

  const onLeadersContinue = useCallback(() => {
    setScreen('game');
  }, []);

  if (screen === 'leaders') {
    return (
      <MonthlyLeadersScreen
        game="snake"
        scoreLabel="ניקוד"
        onContinue={onLeadersContinue}
      />
    );
  }

  return (
    <div className="snake-page" dir="rtl">
      <div className="snake-header">
        <div className="snake-title">נחש</div>
        <div className="snake-score-board">
          <div className="snake-score-item">
            <span className="snake-score-label">ניקוד</span>
            <span className="snake-score-value">{displayScore}</span>
          </div>
          <div className="snake-score-item">
            <span className="snake-score-label">שיא</span>
            <span className="snake-score-value">{displayHigh}</span>
          </div>
        </div>
      </div>

      <div className="snake-canvas-wrapper">
        <canvas ref={canvasRef} width={canvasW} height={canvasH} />

        {phase === 'dead' && (
          <div className="snake-overlay">
            <div className="snake-overlay-title">סוף המשחק</div>
            <div className="snake-overlay-score">ניקוד: {displayScore}</div>
            {displayHigh > 0 && (
              <div className="snake-overlay-subtitle">שיא: {displayHigh}</div>
            )}
            {deadStep === 'loading' && (
              <div className="minisite-overlay-busy">בודקים זכאות לטבלה…</div>
            )}
            {deadStep === 'restartOnly' && (
              <div className="snake-overlay-hint">לחץ Enter להתחלה מחדש</div>
            )}
            {deadStep === 'joinAsk' && (
              <div className="minisite-join">
                <p className="minisite-join-q">להצטרף למצטייני החודש?</p>
                <div className="minisite-join-options">
                  <span className={`minisite-join-opt ${joinSel === 'no' ? 'minisite-join-opt--on' : ''}`}>לא</span>
                  <span className={`minisite-join-opt ${joinSel === 'yes' ? 'minisite-join-opt--on' : ''}`}>כן</span>
                </div>
                <div className="snake-overlay-hint">← → לבחירה · Enter לאישור</div>
              </div>
            )}
            {deadStep === 'name' && (
              <RemoteNameKeyboard
                value={nameInput}
                onChange={setNameInput}
                onDone={handleNameDone}
                onCancel={handleNameCancel}
              />
            )}
            {deadStep === 'thanks' && (
              <div className="snake-overlay-hint">נרשמת בהצלחה! לחץ Enter לחזרה למצטייני החודש</div>
            )}
            {submitting && <div className="minisite-overlay-busy">שומר…</div>}
          </div>
        )}
      </div>

      <div className="snake-footer">↑ ↓ ← → לזוז &nbsp;·&nbsp; Enter להתחלה</div>
    </div>
  );
}

export default SnakeGame;
